import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';

// Demo users for role switching (in-session, no DB hit)
export const DEMO_USERS = {
  super_admin: { _id: 'demo-super-admin', email: 'superadmin@demo.com', firstName: 'Super', lastName: 'Admin',   roleKey: 'super_admin', role: 'super_admin', isActive: true },
  admin:       { _id: 'demo-admin',       email: 'admin@demo.com',      firstName: 'Admin', lastName: 'User',    roleKey: 'admin',       role: 'admin',       isActive: true },
  manager:     { _id: 'demo-manager',     email: 'manager@demo.com',    firstName: 'Manager', lastName: 'User',  roleKey: 'manager',     role: 'manager',     isActive: true },
  team_lead:   { _id: 'demo-teamlead',    email: 'teamlead@demo.com',   firstName: 'Team', lastName: 'Lead',     roleKey: 'team_lead',   role: 'team_lead',   isActive: true },
  faculty:     { _id: 'demo-faculty',     email: 'faculty@demo.com',    firstName: 'Faculty', lastName: 'User',  roleKey: 'faculty',     role: 'faculty',     isActive: true },
  teacher:     { _id: 'demo-teacher',     email: 'teacher@demo.com',    firstName: 'Teacher', lastName: 'User',  roleKey: 'teacher',     role: 'teacher',     isActive: true },
  student:     { _id: 'demo-student',     email: 'student@demo.com',    firstName: 'Student', lastName: 'User',  roleKey: 'student',     role: 'student',     isActive: true },
  parent:      { _id: 'demo-parent',      email: 'parent@demo.com',     firstName: 'Parent', lastName: 'User',   roleKey: 'parent',      role: 'parent',      isActive: true },
};

const DemoContext = createContext(null);

export const useDemo = () => {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used within a DemoProvider');
  return ctx;
};

export const DemoProvider = ({ children }) => {
  const { user: actualUser } = useAuth();

  // ── Demo availability (from backend config) ──────────────────────────────
  const [isDemoSuperAdminAvailable, setIsDemoSuperAdminAvailable] = useState(false);
  const [isDemoIndustryAvailable,   setIsDemoIndustryAvailable]   = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    api.get('/config/demo-mode')
      .then(r => {
        const d = r.data?.data || r.data || {};
        setIsDemoSuperAdminAvailable(Boolean(d.superAdmin));
        setIsDemoIndustryAvailable(Boolean(d.industry));
      })
      .catch(() => {
        // default off if config endpoint unreachable
        setIsDemoSuperAdminAvailable(false);
        setIsDemoIndustryAvailable(false);
      })
      .finally(() => setConfigLoading(false));
  }, []);

  /** True if ANY demo mode is enabled in backend */
  const isDemoAvailable = isDemoSuperAdminAvailable || isDemoIndustryAvailable;

  // ── Demo tenant data (modules + roles) — loaded per selected demo tenant ──
  const [demoTenantSlug,    setDemoTenantSlug]    = useState('bright-future-academy');
  const [demoTenantName,    setDemoTenantName]    = useState('');
  const [demoTenantModules, setDemoTenantModules] = useState([]);
  const [demoTenantRoles,   setDemoTenantRoles]   = useState([]);

  const loadDemoTenantData = useCallback(async (slug = 'bright-future-academy') => {
    try {
      const r = await api.get(`/auth/demo-tenant-modules?slug=${slug}`);
      const d = r.data?.data || r.data || {};
      setDemoTenantModules(d.modules || []);
      setDemoTenantRoles(d.roles   || []);
      setDemoTenantName(d.tenantName || '');
      setDemoTenantSlug(slug);
    } catch { /* non-critical */ }
  }, []);

  // Load demo tenant data when demo mode becomes available
  useEffect(() => {
    if (isDemoAvailable) loadDemoTenantData(demoTenantSlug);
  }, [isDemoAvailable]); // eslint-disable-line

  // ── In-session role switching state ──────────────────────────────────────
  const [demoMode, setDemoMode]               = useState(false);
  const [currentDemoUser, setCurrentDemoUser] = useState(null);
  const [roleSwitchHistory, setRoleSwitchHistory] = useState([]);

  // ── Effective user ───────────────────────────────────────────────────────
  const getEffectiveUser = useCallback(() =>
    demoMode ? currentDemoUser : actualUser,
  [demoMode, currentDemoUser, actualUser]);

  const getEffectiveRole = useCallback(() =>
    getEffectiveUser()?.roleKey || getEffectiveUser()?.role,
  [getEffectiveUser]);

  // ── Switch to demo role ───────────────────────────────────────────────────
  const switchToRole = useCallback((role) => {
    if (!isDemoAvailable) return { success: false, message: 'Demo mode is not available.' };
    if (!DEMO_USERS[role]) return { success: false, message: `Invalid role: ${role}` };

    if (actualUser && !demoMode) {
      setRoleSwitchHistory(prev => [...prev, { user: actualUser, timestamp: new Date() }]);
    }
    setCurrentDemoUser(DEMO_USERS[role]);
    setDemoMode(true);
    console.log(`[Demo] Switched to role: ${role}`);
    return { success: true };
  }, [actualUser, demoMode, isDemoAvailable]);

  const exitDemoMode = useCallback(() => {
    setDemoMode(false);
    setCurrentDemoUser(null);
    setRoleSwitchHistory([]);
  }, []);

  const checkDemoMode = useCallback(() => demoMode, [demoMode]);

  // ── Backend demo mode toggles (super-admin only) ─────────────────────────
  const setBackendDemoMode = useCallback(async (mode, enabled) => {
    try {
      const r = await api.put('/config/demo-mode', { mode, enabled });
      const d = r.data?.data || {};
      setIsDemoSuperAdminAvailable(Boolean(d.superAdmin));
      setIsDemoIndustryAvailable(Boolean(d.industry));
      if (!enabled && demoMode) exitDemoMode();
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, message: e.response?.data?.message || 'Failed to set demo mode' };
    }
  }, [demoMode, exitDemoMode]);

  const toggleBackendDemoMode = useCallback(async (mode) => {
    try {
      const r = await api.post('/config/demo-mode/toggle', { mode });
      const d = r.data?.data || {};
      setIsDemoSuperAdminAvailable(Boolean(d.superAdmin));
      setIsDemoIndustryAvailable(Boolean(d.industry));
      const newState = mode === 'superAdmin' ? d.superAdmin : d.industry;
      if (!newState && demoMode) exitDemoMode();
      return { success: true, data: r.data };
    } catch (e) {
      return { success: false, message: e.response?.data?.message || 'Failed to toggle demo mode' };
    }
  }, [demoMode, exitDemoMode]);

  const value = {
    // State
    demoMode,
    isDemoAvailable,
    isDemoSuperAdminAvailable,
    isDemoIndustryAvailable,
    currentDemoUser,
    roleSwitchHistory,
    loading: configLoading,
    DEMO_USERS,
    // Demo tenant data (for sidebar module display when SA is in demo mode)
    demoTenantSlug,
    demoTenantName,
    demoTenantModules,
    demoTenantRoles,
    // Functions
    switchToRole,
    exitDemoMode,
    getEffectiveUser,
    getEffectiveRole,
    checkDemoMode,
    setBackendDemoMode,
    toggleBackendDemoMode,
    selectDemoTenant: loadDemoTenantData,
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};

export default DemoContext;
